import React, { useEffect } from 'react';
import { Template, WorkflowItem } from '../../store/interfaces';
import { getExperienceInitCookie } from '../../common/utils/experienceInitCookie';
import { routeMappings, SessionStore } from '../../constants';
import { RoutePageProps } from '../../base/components/Routes';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../store/hook';
import { getCookie } from '../../common/utils/cookies';
import { handleTCPlusRedirection } from '../../common/utils/handleTCPlusRedirection';

/** Method to redirect to current workflow item or login page conditionally */
const redirectUrlForNonAuthenticatedUser = (
  basicDetails: {
    currentWorkflowItem: WorkflowItem;
    userSessionActive: boolean;
  },
  template: Template,
): string => {
  const tcPlusFlow = getCookie('TcPlusFlag') === '1';
  const { currentWorkflowItem, userSessionActive } = basicDetails;
  /** userSessionActive has highest priority, then login and after that all the rest */
  let url = routeMappings['NOT_FOUND'];
  if (currentWorkflowItem) {
    url = `${routeMappings[currentWorkflowItem.code]}/${currentWorkflowItem.id}`;
    if (!userSessionActive) {
      url = routeMappings['LOGIN'];
      if (tcPlusFlow) url = routeMappings['AUTH_LOGIN'];
    } else if (userSessionActive && tcPlusFlow) {
      /* handle browser back on simplified forms */
      url = handleTCPlusRedirection(url, template);
    }
  }
  return url;
};

/**
 * - HOC Responsible to fetch basic details from sessionstorage
 */
export const withNoAuthentication =
  <P extends RoutePageProps>(Component: React.ComponentType<P>): ((props: P) => React.JSX.Element) =>
  (props: P): React.JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();
    const expCookie = getExperienceInitCookie();
    const deadlineDatePassed = (expCookie && expCookie.candidateDeadlinePassed) || false;
    const { userSessionActive } = useAppSelector((state) => state.basicDetails);
    const template = useAppSelector((state) => state.template);
    const currentWorkflowItem = useAppSelector((state) => state.currentWorkflowItem);
    const isIntegrated = sessionStorage.getItem(SessionStore.isIntegrated);
    const isUserLoggedOut = sessionStorage.getItem(SessionStore.loggedoutuser);
    useEffect(() => {
      if (deadlineDatePassed) {
        navigate(routeMappings['EXPIRED']);
        return;
      }
      // CAW-7369 : For integrated flow if user redirects to another external link and press back button from there, redirects to sign out page.
      if (isIntegrated === 'true' && isUserLoggedOut === 'true') {
        navigate(routeMappings['AUTH_LOGOUT']);
        return;
      }
      if (currentWorkflowItem && template.id) {
        if (![routeMappings['FORGOT_PASSWORD'], routeMappings['AUTH_FORGOT_PASSWORD']].includes(location.pathname)) {
          navigate(redirectUrlForNonAuthenticatedUser({ currentWorkflowItem, userSessionActive }, template));
        }
      } else {
        // TODO: If we have feature to auto-initialize the workflow session
        navigate(routeMappings['NOT_FOUND']);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, location.pathname]);

    return (
      <>
        <React.Suspense fallback={<></>}>
          <Component {...props} />
        </React.Suspense>
      </>
    );
  };
